import { useState } from "react";
import { toast } from "sonner";
import { Store, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const ListingCard = ({ profile }) => {
  const [busy, setBusy] = useState(false);
  const [autoRenew, setAutoRenew] = useState(!!profile?.listing_auto_renew);
  if (!profile || profile.status !== "approved") return null;

  const active = profile.listing_until && new Date(profile.listing_until) > new Date();
  const until = active ? new Date(profile.listing_until).toLocaleDateString("en-CA") : null;
  const freeUntilDate = profile.free_listing_until ? new Date(profile.free_listing_until) : null;
  const freeYear = active && !autoRenew && freeUntilDate && freeUntilDate > new Date();
  const freeYearEnded = !active && !!freeUntilDate;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/listing/checkout", { origin_url: window.location.origin });
      if (data.resumed) {
        setAutoRenew(true);
        toast.success("Auto-renew resumed — your listing will keep renewing monthly ✦");
        setBusy(false);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const cancelRenewal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/listing/cancel-renewal");
      setAutoRenew(false);
      toast.success(`Auto-renew cancelled — your listing stays active until ${data.active_until ? new Date(data.active_until).toLocaleDateString("en-CA") : "the end of your paid month"}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`silk-card rounded-lg p-5 mb-4 flex flex-wrap items-center justify-between gap-4 ${active ? "" : "border border-foreground/30"}`} data-testid="listing-card">
      <div className="flex items-start gap-3">
        {active ? <Store size={20} className="text-primary mt-0.5" /> : <AlertTriangle size={20} className="text-foreground/80 mt-0.5" />}
        <div>
          <p className="text-foreground text-sm font-medium">Leader listing</p>
          {freeYear ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="listing-status">
              Free for your first year — your profile is live and bookable through {until}, nothing to pay. After that it's $15 CAD / month to stay listed; we'll remind you before then.
            </p>
          ) : active && autoRenew ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="listing-status">
              Renews automatically each month ($15 CAD) — paid through {until}. Your profile is visible and bookable.
            </p>
          ) : active ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="listing-status">
              Auto-renew is off — active until {until}, then your profile will be hidden
            </p>
          ) : (
            <p className="text-xs text-foreground/80 mt-1" data-testid="listing-status">
              {freeYearEnded ? "Your free first year has ended — your profile is hidden from seekers until you activate your listing" : "Your profile is hidden from seekers until your listing is active"} — $15 CAD / month, renews automatically, cancel anytime
            </p>
          )}
        </div>
      </div>
      {freeYear ? null : active && autoRenew ? (
        <Button size="sm" variant="outline" className="rounded-full px-6"
          data-testid="listing-cancel-renewal-btn" disabled={busy} onClick={cancelRenewal}>
          {busy ? "One moment…" : "Cancel auto-renew"}
        </Button>
      ) : (
        <Button size="sm" variant={active ? "outline" : "default"} className="rounded-full px-6"
          data-testid="listing-buy-btn" disabled={busy} onClick={subscribe}>
          {busy ? "One moment…" : active ? "Resume auto-renew — $15/mo" : "Activate listing — $15/mo"}
        </Button>
      )}
    </div>
  );
};
